import { motion } from "framer-motion";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { MessageSquare, Search, Wrench } from "lucide-react";

const CTA_URL = "https://diyb.ca/contact-diyb/";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const steps = [
  {
    num: "01",
    icon: MessageSquare,
    title: "You Contact DIYB",
    description: "Reach out through the contact page. Simple. No pressure. No sales pitch.",
  },
  {
    num: "02",
    icon: Search,
    title: "I Ask Sharp Questions",
    description: "I need to understand the leak. A few focused questions so I can see where the system is loose.",
  },
  {
    num: "03",
    icon: Wrench,
    title: "I Recommend & Install",
    description: "I recommend the right Smart Digital Doorway. Then I install it. You get a real plan and a real install.",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative py-24 md:py-36 bg-[#020617]"
    >
      <div className="blueprint-grid absolute inset-0 opacity-10" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <motion.p
          {...fadeInUp}
          transition={{ duration: 0.6 }}
          className="font-body text-fluid-caption tracking-[0.3em] uppercase text-gold mb-4"
        >
          What Happens Next
        </motion.p>

        <motion.h2
          {...fadeInUp}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-heading font-bold text-fluid-h2 uppercase tracking-tight text-slate-50 mb-4"
        >
          Simple. Clear. <span className="text-gold-gradient">Fast.</span>
        </motion.h2>

        <Separator className="bg-gold/20 mb-16 mx-auto max-w-xs" />

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.num}
                data-testid={`step-card-${i}`}
                className="relative text-left"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2, duration: 0.6 }}
              >
                {/* Step Number */}
                <span className="font-heading font-black text-7xl text-gold/10 absolute -top-4 -left-2">
                  {step.num}
                </span>

                <div className="relative pt-8">
                  <div className="w-14 h-14 rounded-sm bg-gold/10 border border-gold/20 flex items-center justify-center mb-5">
                    <Icon size={26} className="text-gold" />
                  </div>

                  <h3 className="font-heading font-bold text-fluid-h3 text-slate-50 uppercase tracking-wide mb-3">
                    {step.title}
                  </h3>

                  <p className="font-body text-fluid-body text-slate-400">
                    {step.description}
                  </p>
                </div>

                {/* Connector Line (not on last item) */}
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 border-t border-dashed border-gold/20" />
                )}
              </motion.div>
            );
          })}
        </div>

        <motion.div
          {...fadeInUp}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <a href={CTA_URL} target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="how-it-works-cta-button"
              className="bg-gold hover:bg-gold-300 text-navy-500 font-heading font-bold tracking-wider uppercase text-base px-8 py-5 rounded-sm transition-all duration-300 hover:shadow-[0_0_25px_-5px_rgba(212,175,55,0.5)]"
            >
              Start Here
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
